"use client";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Mail, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function LetsMakeSomeNoise() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const animationFrameRef = useRef<number>();
  const timeRef = useRef(0);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    const animate = () => {
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      timeRef.current += 0.02;
      ctx.fillStyle = "hsl(210, 12%, 17%)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const centerY = canvas.height / 2;
      const waves = 3;
      for (let w = 0; w < waves; w++) {
        ctx.beginPath();
        const color = w === 0 ? "hsl(210, 12%, 17%)" : w === 1 ? "hsl(330, 85%, 48%)" : "hsl(80, 95%, 58%)";
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.shadowColor = color;
        ctx.shadowBlur = 15 + Math.sin(timeRef.current + w) * 5;
        for (let x = 0; x < canvas.width; x += 2) {
          const distX = Math.abs(x - mousePos.x);
          const distY = Math.abs(centerY - mousePos.y);
          const dist = Math.sqrt(distX * distX + distY * distY);
          const influence = Math.max(0, 1 - dist / 300);
          const amplitude = 40 + influence * 60;
          const frequency = 0.01 + w * 0.005;
          const y = centerY + Math.sin(x * frequency + timeRef.current + w) * amplitude + (w - 1) * 50;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mousePos]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden" onMouseMove={handleMouseMove}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{
      display: "block"
    }} />

      <div ref={contentRef} className="relative z-10 text-center px-4" style={{
      transform: isVisible ? "scale(1)" : "scale(0.8)",
      opacity: isVisible ? 1 : 0,
      transition: "all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)"
    }}>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-tight">
          Let's Make Some{" "}
          <span className="text-accent drop-shadow-[0_0_20px_hsl(80,95%,58%)]">
            Noise!
          </span>
        </h1>

        <Dialog>
          <DialogTrigger asChild>
            <Button size="lg" className="text-lg px-12 py-6 h-auto bg-dark-background hover:bg-dark-background/90 text-foreground-dark font-bold rounded-lg shadow-[0_0_30px_hsl(210,12%,17%)] hover:shadow-[0_0_40px_hsl(210,12%,17%)] hover:scale-105 active:scale-95 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
              Get In Touch
            </Button>
          </DialogTrigger>

          <DialogContent className="bg-dark-background border-border">
            <DialogHeader>
              <DialogTitle className="text-2xl font-display text-white">
                Contact D&J Entertainment
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Ready to bring the party? Reach out to us through any of the channels below.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-4">
              <Link to="tel:(123) 456-7890" className="flex items-center gap-3 p-4 rounded-md bg-dark-background/10 border border-border hover:bg-dark-background/20 transition-all duration-[var(--duration-medium)]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-5 text-foreground-dark" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/LetsMakeSomeNoise.tsx|sig:fyhe3o" data-ast-start="5093" data-ast-end="5142"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg><span className="text-white font-medium" data-ast-id="f:/src/pages/Home/sections/LetsMakeSomeNoise.tsx|sig:gezba6" data-ast-start="5159" data-ast-end="5221">(636) 235-1115</span></Link>

              <Link to="mailto:contact@djentertainment.com" className="flex items-center gap-3 p-4 rounded-md bg-secondary/10 border border-secondary/30 hover:bg-secondary/20 transition-all duration-[var(--duration-medium)]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-mail size-5 text-secondary" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/LetsMakeSomeNoise.tsx|sig:6m15ey" data-ast-start="5945" data-ast-end="5987"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></svg><span className="text-white font-medium" data-ast-id="f:/src/pages/Home/sections/LetsMakeSomeNoise.tsx|sig:gezba6" data-ast-start="6004" data-ast-end="6079">joseph.d.brooks@gmail.com</span></Link>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>;
}